import type { Route } from "vue-router"

const cleanCharactersMask = /[^a-z0-9\-_]/g
const noEmptyEqualsMask = /=(?=$|&)/g
const pathSeparator = "/"
const domainMask = /^[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(\.[a-zA-Z0-9]([a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*\.[a-zA-Z]{2,}$/

export function setInQuery(url: string, parameters?: Record<string, any>): string {
	if (!parameters)
		return url

	const urlSplit = url.split("?")

	const query = new URLSearchParams(
		urlSplit.length > 1
			? urlSplit[1]
			: undefined)

	Object.entries(parameters).forEach(([key, value]) => {
		if (value === null)
			query.set(key, "")
		else if (value !== undefined)
			query.set(key, value.toString())
	})

	const queryString = query.toString().replaceAll(noEmptyEqualsMask, "")

	return queryString !== ""
		? `${urlSplit[0]}?${queryString}`
		: urlSplit[0]
}

export function toUrlCleanString(value: string): string {
	return value.toLocaleLowerCase().replaceAll(cleanCharactersMask, "")
}

export function urlEncode(url: string): string {
	return encodeURIComponent(url)
}

export function getPathComponents(url: string): string[] {
	return url.split(pathSeparator).filter(component => component !== "")
}

export function getLastPathComponent(url: string): string {
	const components = getPathComponents(url)

	return components.length === 0
		? ""
		: components[components.length - 1]
}

export function getAncestorPathComponent(url: string, ancestorDistance: number = 1): string {
	const components = getPathComponents(url)

	if (components.length <= ancestorDistance)
		return ""

	return components[components.length - ancestorDistance - 1]
}

export function getAncestorPathComponentByLevel(url: string, level: number = 0): string {
	const components = getPathComponents(url)

	if (components.length <= level)
		return components.length === 0 ? "" : components[components.length - 1]

	return components[level]
}

export function getAncestorPath(url: string, ancestorDistance: number = 1): string {
	const components = getPathComponents(url)

	if (components.length <= ancestorDistance)
		return pathSeparator

	components.splice(components.length - ancestorDistance)
	return (url.startsWith(pathSeparator) ? pathSeparator : "")
		+ components.join(pathSeparator)
}

export function routeEndsWithParameter(route: Route): boolean {
	if (route.matched.length === 0)
		return false

	const lastMatched = route.matched[route.matched.length - 1]

	const components = getPathComponents(lastMatched.path)

	if (components.length === 0)
		return false

	let lastComponent = components[components.length - 1]
	if (!lastComponent.startsWith(":"))
		return false

	if (lastComponent.endsWith("?"))
		lastComponent = lastComponent.slice(1, -1)
	else
		lastComponent = lastComponent.slice(1)

	return route.params[lastComponent] !== undefined
}

export function isValidWebUrl(value: string): boolean {
	try {
		const url = new URL(value)

		if (url.protocol !== "https:" && url.protocol !== "http:")
			return false

		return domainMask.test(url.hostname)
	} catch {
		return false
	}
}
